import type {
	AddTeacherPayload,
	GetMoreSeatsPayload,
	InviteStripeTeacherPayload,
	InviteStripeTeachersBatchPayload,
} from '@kimuno/api-sdk';

import api from '../clients/api';
import { handleUnauthorized } from '../utils/apiWrapper';
import { notifyBugsnagNetwork } from '../utils/bugsnagClient';

const service = {
	addTeacher,
	updateTeacher,
	getSchoolInvoices,
	getSchoolInvoiceDownloadLink,
	getSchoolTeachers,
	getSchoolSeatsOverview,
	getStripeSchoolTeachers,
	getStripeSchoolSeats,
	inviteStripeSchoolTeacher,
	inviteStripeSchoolTeachersBatch,
	removeStripeSchoolTeacher,
	upgradeSubscription,
	resendInvitation,
	resetPassword,
	removeTeacher,
	removeSchoolAdmin,
	getSchoolAdmins,
	assignSchoolAdmin,
	requestMoreSeats,
	requestUpgradeProducts,
	createSchoolAdmin,
};

function getSchoolInvoices(schoolId: string, page = 1, pageSize = 10) {
	notifyBugsnagNetwork('school', 'getSchoolInvoices');
	return handleUnauthorized(() => api.school.getSchoolInvoices(schoolId, page, pageSize));
}

function getSchoolInvoiceDownloadLink(schoolId: string, invoiceId: string) {
	notifyBugsnagNetwork('school', 'getSchoolInvoiceDownloadLink');
	return handleUnauthorized(() => api.school.getSchoolInvoiceDownloadLink(schoolId, invoiceId));
}

function getSchoolTeachers(
	schoolId: string,
	page = 1,
	pageSize = 20,
	search = '',
	refreshCache = false
) {
	notifyBugsnagNetwork('school', 'getSchoolTeachers');
	return handleUnauthorized(() =>
		api.school.getSchoolTeachers(schoolId, page, pageSize, search, refreshCache)
	);
}

function getSchoolSeatsOverview(schoolId: string) {
	notifyBugsnagNetwork('school', 'getSchoolSeatsOverview');
	return handleUnauthorized(() => api.school.getSchoolSeatsOverview(schoolId));
}

function getStripeSchoolTeachers(schoolId: string, page = 1, pageSize = 50, search = '') {
	notifyBugsnagNetwork('school', 'getStripeSchoolTeachers');
	return handleUnauthorized(() =>
		api.school.getStripeSchoolTeachers(schoolId, page, pageSize, search)
	);
}

function getStripeSchoolSeats(schoolId: string) {
	notifyBugsnagNetwork('school', 'getStripeSchoolSeats');
	return handleUnauthorized(() => api.school.getStripeSchoolSeats(schoolId));
}

function inviteStripeSchoolTeacher(schoolId: string, payload: InviteStripeTeacherPayload) {
	notifyBugsnagNetwork('school', 'inviteStripeSchoolTeacher');
	return handleUnauthorized(() => api.school.inviteStripeSchoolTeacher(schoolId, payload));
}

function inviteStripeSchoolTeachersBatch(
	schoolId: string,
	payload: InviteStripeTeachersBatchPayload
) {
	notifyBugsnagNetwork('school', 'inviteStripeSchoolTeachersBatch');
	return handleUnauthorized(() => api.school.inviteStripeSchoolTeachersBatch(schoolId, payload));
}

function removeStripeSchoolTeacher(schoolId: string, muUserId: string) {
	notifyBugsnagNetwork('school', 'removeStripeSchoolTeacher');
	return handleUnauthorized(() => api.school.removeStripeSchoolTeacher(schoolId, muUserId));
}

function upgradeSubscription(
	schoolId: string,
	accountNumber: string,
	newLicenceId: number,
	subscriptionId: string,
	userId: string
) {
	notifyBugsnagNetwork('school', 'upgradeSubscription');

	return handleUnauthorized(() =>
		api.school.upgradeSubscription(schoolId, accountNumber, newLicenceId, subscriptionId, userId)
	)
		.then((result) => result)
		.catch((error) => ({ success: false, error }));
}

function addTeacher(schoolId: string, teacher: AddTeacherPayload) {
	notifyBugsnagNetwork('school', 'addTeacher');
	return handleUnauthorized(() => api.school.addTeacher(schoolId, teacher));
}

function updateTeacher(
	userId: string,
	schoolId: string,
	firstName: string,
	lastName: string,
	email: string
) {
	notifyBugsnagNetwork('school', 'updateTeacher');
	return handleUnauthorized(() =>
		api.school.updateTeacherInfo(userId, schoolId, {
			firstName,
			lastName,
			email,
		})
	);
}

function resendInvitation(schoolId: string, userId: string, licenceModelId?: number) {
	notifyBugsnagNetwork('school', 'resendInvitation');
	return handleUnauthorized(() => api.school.resendInvitation(schoolId, userId, licenceModelId));
}

function resetPassword(schoolId: string, email: string) {
	notifyBugsnagNetwork('school', 'resetPassword');
	return handleUnauthorized(() => api.school.resetPassword(schoolId, email));
}

function removeTeacher(userId: string, schoolId: string) {
	notifyBugsnagNetwork('school', 'removeTeacher');
	return handleUnauthorized(() => api.school.removeTeacher(userId, schoolId));
}

function removeSchoolAdmin(userId: string, schoolId: string) {
	notifyBugsnagNetwork('school', 'removeSchoolAdmin');
	return handleUnauthorized(() => api.school.removeSchoolAdmin(userId, schoolId));
}

function getSchoolAdmins(schoolId: string) {
	notifyBugsnagNetwork('school', 'getSchoolAdmins');
	return handleUnauthorized(() => api.school.getSchoolAdmins(schoolId));
}

function assignSchoolAdmin(userId: string, schoolId: string) {
	notifyBugsnagNetwork('school', 'assignSchoolAdmin');
	return handleUnauthorized(() => api.school.assignSchoolAdmin(userId, schoolId));
}

function requestMoreSeats(payload: GetMoreSeatsPayload) {
	notifyBugsnagNetwork('school', 'requestMoreSeats');
	return handleUnauthorized(() => api.school.getMoreSeats(payload));
}

function requestUpgradeProducts(payload: { schoolId: string; requestedProducts: string[] }) {
	notifyBugsnagNetwork('school', 'requestUpgradeProducts');
	return handleUnauthorized(() =>
		api.school.requestUpgradeProducts(payload.schoolId, payload.requestedProducts)
	);
}

function createSchoolAdmin(admin: {
	firstName: string;
	lastName: string;
	email: string;
	schoolId: string;
}) {
	notifyBugsnagNetwork('school', 'createSchoolAdmin');
	return handleUnauthorized(() => api.school.createSchoolAdmin(admin));
}

export default service;
